import { Router } from 'express';
import { EventEmitter } from 'events';
import chokidar from 'chokidar';
import path from 'path';
import { logger } from '../../utils/logger.js';

export const docsRouter = Router();

// 文档事件发射器
const docEventBus = new EventEmitter();

// 客户端连接管理
interface DocSSEClient {
  id: string;
  res: any;
  workspaceId?: string;
}

const docClients = new Map<string, DocSSEClient>();

// 文档目录监视器
let watcher: chokidar.FSWatcher | null = null;

// 初始化文档监视器
function initDocWatcher() {
  if (watcher) {
    return;
  }

  // 监视常见的文档目录
  const docDirs = [
    path.join(process.cwd(), 'docs'),
    path.join(process.cwd(), 'README.md'),
    path.join(process.cwd(), '.workflow', 'docs'),
    path.join(process.cwd(), 'content')
  ];

  watcher = chokidar.watch(docDirs, {
    ignored: /(^|[\/\\])\../, // 忽略隐藏文件
    persistent: true,
    ignoreInitial: true
  });

  // 文件添加
  watcher.on('add', (filePath) => {
    if (isDocumentFile(filePath)) {
      docEventBus.emit('file-change', {
        type: 'add',
        filePath,
        fileName: path.basename(filePath)
      });
    }
  });

  // 文件修改
  watcher.on('change', (filePath) => {
    if (isDocumentFile(filePath)) {
      docEventBus.emit('file-change', {
        type: 'change',
        filePath,
        fileName: path.basename(filePath)
      });
    }
  });

  // 文件删除
  watcher.on('unlink', (filePath) => {
    if (isDocumentFile(filePath)) {
      docEventBus.emit('file-change', {
        type: 'unlink',
        filePath,
        fileName: path.basename(filePath)
      });
    }
  });

  logger.info('Document watcher initialized');
}

// 检查是否是文档文件
function isDocumentFile(filePath: string): boolean {
  const ext = path.extname(filePath).toLowerCase();
  return ['.md', '.mdx', '.txt', '.rst', '.adoc'].includes(ext);
}

// SSE 事件流端点
docsRouter.get('/events', (req, res) => {
  // 设置 SSE 响应头
  res.writeHead(200, {
    'Content-Type': 'text/event-stream',
    'Cache-Control': 'no-cache',
    'Connection': 'keep-alive',
    'X-Accel-Buffering': 'no'
  });

  // 创建客户端 ID
  const clientId = `doc-sse-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const workspaceId = req.query.workspaceId as string;

  // 保存客户端连接
  const client: DocSSEClient = {
    id: clientId,
    res,
    workspaceId
  };
  docClients.set(clientId, client);

  // 发送连接成功消息
  res.write(`event: connected\ndata: ${JSON.stringify({ 
    type: 'connected', 
    clientId,
    timestamp: new Date().toISOString()
  })}\n\n`);

  // 初始化文档监视器
  initDocWatcher();

  // 心跳包
  const heartbeatInterval = setInterval(() => {
    res.write(`event: heartbeat\ndata: ${JSON.stringify({ 
      type: 'heartbeat',
      timestamp: new Date().toISOString()
    })}\n\n`);
  }, 30000); // 每 30 秒发送一次心跳

  // 监听文档变化事件
  const fileChangeHandler = (data: any) => {
    res.write(`event: file-change\ndata: ${JSON.stringify({
      type: 'file-change',
      data,
      timestamp: new Date().toISOString()
    })}\n\n`);
  };

  const refreshHandler = () => {
    res.write(`event: refresh\ndata: ${JSON.stringify({
      type: 'refresh',
      timestamp: new Date().toISOString()
    })}\n\n`);
  };

  // 订阅事件
  docEventBus.on('file-change', fileChangeHandler);
  docEventBus.on('refresh', refreshHandler);

  // 处理客户端断开
  req.on('close', () => {
    clearInterval(heartbeatInterval);
    docClients.delete(clientId);
    docEventBus.off('file-change', fileChangeHandler);
    docEventBus.off('refresh', refreshHandler);
    logger.info(`Document SSE client disconnected: ${clientId}`);
  });

  // 处理错误
  req.on('error', (error) => {
    logger.error(`Document SSE client error: ${clientId}`, error);
    clearInterval(heartbeatInterval);
    docClients.delete(clientId);
    docEventBus.off('file-change', fileChangeHandler);
    docEventBus.off('refresh', refreshHandler);
  });
});

// 手动刷新端点
docsRouter.post('/refresh', (req, res) => {
  docEventBus.emit('refresh');
  res.json({ 
    success: true, 
    message: 'Refresh event sent',
    connectedClients: docClients.size
  });
});

// 获取连接的客户端信息
docsRouter.get('/clients', (req, res) => {
  const clients = Array.from(docClients.values()).map(client => ({
    id: client.id,
    workspaceId: client.workspaceId,
    connected: true
  }));
  
  res.json({
    total: clients.length,
    clients
  });
});

// 清理函数
export function cleanupDocWatcher() {
  if (watcher) {
    watcher.close();
    watcher = null;
    logger.info('Document watcher closed');
  }
}